import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { Action } from '../../../actions';
import { EditorType, ZoomType } from '../constants';
import { getClientAndResourceIds } from '../utils/helpers';
import { createNotification } from '../../NotificationBar/actions';
import { updateDriveItemName } from '../../DriveItemList/actions';
import UrlUtils from '../../../utils/url';
import ApiUtils from '../../../utils/api';
import UserAgentUtils from '../../../utils/userAgent';
import NativeApi from '../../../utils/nativeApi';
import { getData } from './sources';
import { checkActiveSession } from '../../MultiAppAuthentication/actions';
import Utils from '../../../utils';

export enum UiActionKeys {
	SET_EDITOR_TYPE = '@@editor/ui/SET_EDITOR_TYPE',
	SET_IS_EDITOR_LOADED = '@@editor/ui/SET_IS_EDITOR_LOADED',
	TOGGLE_EDIT_PANEL = '@@editor/ui/TOGGLE_EDIT_PANEL',
	MOVE_EDIT_PANEL = '@@editor/ui/MOVE_EDIT_PANEL',
	SET_ACTIVE_ITEM = '@@editor/ui/SET_ACTIVE_ITEM',
	RESET_ACTIVE_ITEM = '@@editor/ui/RESET_ACTIVE_ITEM',
	SET_LABEL = '@@editor/ui/SET_LABEL',
	SET_IS_RENAMING = '@@editor/ui/SET_IS_RENAMING',
	RENAME = '@@editor/ui/RENAME',
	SET_RENAME_ERROR = '@@editor/ui/SET_RENAME_ERROR',
	SET_VERSION = '@@editor/ui/SET_VERSION',
	ZOOM_PAGE = '@@editor/ui/ZOOM_PAGE',
	SHOW_LOCKED_EDITOR_POPUP = '@@editor/ui/SHOW_LOCKED_EDITOR_POPUP',
	HIDE_LOCKED_EDITOR_POPUP = '@@editor/ui/HIDE_LOCKED_EDITOR_POPUP',
	RESET = '@@editor/ui/RESET',
	SET_IS_ACCESSIBLE_VERSION = '@@editor/ui/SET_IS_ACCESSIBLE_VERSION',
}

interface SetEditorTypeAction extends Action {
	type: UiActionKeys.SET_EDITOR_TYPE;
	editorType: EditorType;
}

interface SetIsEditorLoadedAction extends Action {
	type: UiActionKeys.SET_IS_EDITOR_LOADED;
	isLoaded: boolean;
}

interface ToggleEditPanelAction extends Action {
	type: UiActionKeys.TOGGLE_EDIT_PANEL;
}

interface MoveEditPanelAction extends Action {
	type: UiActionKeys.MOVE_EDIT_PANEL;
	x: number;
	y: number;
}

interface SetActiveItemAction extends Action {
	type: UiActionKeys.SET_ACTIVE_ITEM;
	id: string;
	page: number | null;
	parentId: string | null;
}

interface ResetActiveItemAction extends Action {
	type: UiActionKeys.RESET_ACTIVE_ITEM;
}

interface SetLabelAction extends Action {
	type: UiActionKeys.SET_LABEL;
	label: string;
}

interface SetIsRenamingAction extends Action {
	type: UiActionKeys.SET_IS_RENAMING;
	isRenaming: boolean;
}

interface RenameAction extends Action {
	type: UiActionKeys.RENAME;
	newLabel: string;
}

interface SetRenameErrorAction extends Action {
	type: UiActionKeys.SET_RENAME_ERROR;
	isError: boolean;
}

interface SetVersionAction extends Action {
	type: UiActionKeys.SET_VERSION;
	version: number;
}

interface ZoomPageAction extends Action {
	type: UiActionKeys.ZOOM_PAGE;
	zoom: ZoomType;
}

interface ShowLockedEditorPopupAction extends Action {
	type: UiActionKeys.SHOW_LOCKED_EDITOR_POPUP;
	userName: string;
}

interface HideLockedEditorPopupAction extends Action {
	type: UiActionKeys.HIDE_LOCKED_EDITOR_POPUP;
}

interface ResetAction extends Action {
	type: UiActionKeys.RESET;
	isIntermediate?: boolean; // is needed to keep some editor data when some actions like combine or pages manipulations happen
}

interface SetIsAccessibleVersionAction extends Action {
	type: UiActionKeys.SET_IS_ACCESSIBLE_VERSION;
	isAccessibleVersion: boolean;
}

export type UiAction =
	SetEditorTypeAction |
	SetIsEditorLoadedAction |
	ToggleEditPanelAction |
	MoveEditPanelAction |
	SetActiveItemAction |
	ResetActiveItemAction |
	SetLabelAction |
	SetIsRenamingAction |
	RenameAction |
	SetRenameErrorAction |
	SetVersionAction |
	ZoomPageAction |
	ShowLockedEditorPopupAction |
	HideLockedEditorPopupAction |
	ResetAction |
	SetIsAccessibleVersionAction;

export const setEditorType = (editorType: EditorType): SetEditorTypeAction => ({
	type: UiActionKeys.SET_EDITOR_TYPE,
	editorType,
});

export const setIsEditorLoaded = (isLoaded: boolean): SetIsEditorLoadedAction => ({
	type: UiActionKeys.SET_IS_EDITOR_LOADED,
	isLoaded,
});

export const toggleEditPanel = (): ToggleEditPanelAction => ({
	type: UiActionKeys.TOGGLE_EDIT_PANEL,
});

export const moveEditPanel = (x: number, y: number): MoveEditPanelAction => ({
	type: UiActionKeys.MOVE_EDIT_PANEL,
	x,
	y,
});

export const setActiveItem = (id: string, page: number | null, parentId: string | null): SetActiveItemAction => ({
	type: UiActionKeys.SET_ACTIVE_ITEM,
	id,
	page,
	parentId,
});

export const resetActiveItem = (): ResetActiveItemAction => ({
	type: UiActionKeys.RESET_ACTIVE_ITEM,
});

export const setLabel = (label: string): SetLabelAction => ({
	type: UiActionKeys.SET_LABEL,
	label,
});

export const setIsRenaming = (isRenaming: boolean): SetIsRenamingAction => ({
	type: UiActionKeys.SET_IS_RENAMING,
	isRenaming,
});

export const rename = (newLabel: string): RenameAction => ({
	type: UiActionKeys.RENAME,
	newLabel,
});

export const setRenameError = (isError: boolean): SetRenameErrorAction => ({
	type: UiActionKeys.SET_RENAME_ERROR,
	isError,
});

export const setVersion = (version: number): SetVersionAction => ({
	type: UiActionKeys.SET_VERSION,
	version,
});

export const zoomPage = (zoom: ZoomType): ZoomPageAction => ({
	type: UiActionKeys.ZOOM_PAGE,
	zoom,
});

export const showLockedEditorPopup = (userName: string): ShowLockedEditorPopupAction => ({
	type: UiActionKeys.SHOW_LOCKED_EDITOR_POPUP,
	userName,
});

export const hideLockedEditorPopup = (): HideLockedEditorPopupAction => ({
	type: UiActionKeys.HIDE_LOCKED_EDITOR_POPUP,
});

export const reset = (isIntermediate?: boolean): ResetAction => ({
	type: UiActionKeys.RESET,
	isIntermediate,
});

export const setIsAccessibleVersion = (isAccessibleVersion: boolean): SetIsAccessibleVersionAction => ({
	type: UiActionKeys.SET_IS_ACCESSIBLE_VERSION,
	isAccessibleVersion,
});

export const sendRenameRequest = (label: string) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { clientId, tempGId, originalGId } = getClientAndResourceIds();
		const { newLabel } = await ApiUtils.renameResource(clientId, originalGId, label);
		dispatch(rename(newLabel));
		dispatch(updateDriveItemName(originalGId, newLabel));

		const shouldUpdatePreviewName = tempGId !== originalGId;
		if (shouldUpdatePreviewName) {
			await ApiUtils.renameResource(clientId, tempGId, label);
			dispatch(updateDriveItemName(tempGId, newLabel));
		}
	} catch (error) {
		dispatch(setRenameError(true));

		if (ApiUtils.isDuplicateFileNameError(error.responseText)) {
			dispatch(createNotification('error.duplicate_file_name'));
		} else {
			dispatch(createNotification('error.rename_asset_error'));
		}
	}
};

export const openPreview = (id: number) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	await dispatch(checkActiveSession());

	const {
		editor: {
			pagesController: { spreads, currentSpreadIndex },
		},
		driveList: { driveItems },
		galleryInfos: { config },
	} = getState();
	const driveItem = driveItems[id];
	const page = spreads[currentSpreadIndex][0];
	const previewUrl = UrlUtils.getPublicationUrl(config, driveItem, page, UserAgentUtils.isMobileApp);

	if (UserAgentUtils.isMobileApp) {
		NativeApi.openFrame(previewUrl);
	} else {
		window.open(previewUrl);
	}
};

export const openAccessibleVersionPreview = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	await dispatch(checkActiveSession());

	const {
		editor: {
			pagesController: { currentSpreadIndex },
			paths: { accessibleUrl },
		},
	} = getState();

	const accessibleUrlWithToken = accessibleUrl ? accessibleUrl + `${Utils.getUrlSeparator(accessibleUrl)}token=${ApiUtils.token}` : '';
	const previewUrl = accessibleUrlWithToken ? accessibleUrlWithToken + `#page=${currentSpreadIndex + 1}` : '';

	if (previewUrl) {
		if (UserAgentUtils.isMobileApp) {
			NativeApi.openFrame(previewUrl);
		} else {
			window.open(previewUrl);
		}
	} else {
		dispatch(createNotification('error.get_accessible_version_url'));
	}
};

export const acquireLock = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const { clientId, tempGId: resourceId } = getClientAndResourceIds();
		dispatch(hideLockedEditorPopup());
		dispatch(reset(true));
		await ApiUtils.acquireLock(clientId, resourceId);
		dispatch(getData(clientId, resourceId));
	} catch (error) {
		dispatch(createNotification('error.fetch_editor_data_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/actions/ui.ts